import type {
  PurchasesOffering,
  PurchasesPackage,
  CustomerInfo,
} from '@revenuecat/purchases-capacitor';

export interface SubscriptionStatus {
  isSubscribed: boolean;
  isPremium: boolean;
  currentPlan?: string;
  expirationDate?: Date;
}

export class PurchasesService {
  private static instance: PurchasesService;
  private isInitialized = false;
  private currentAppUserID?: string;
  private get isNativePlatform(): boolean {
    if (typeof window === 'undefined') return false;
    const cap = (window as any)?.Capacitor;
    if (!cap) return false;
    try {
      if (typeof cap.isNativePlatform === 'function')
        return Boolean(cap.isNativePlatform());
      if (typeof cap.getPlatform === 'function')
        return cap.getPlatform() !== 'web';
      if (typeof cap.platform === 'string') return cap.platform !== 'web';
    } catch {
      // ignore
    }
    return false;
  }
  private customerInfoListenerMap = new Map<
    (customerInfo: CustomerInfo) => void,
    string
  >();

  private constructor() {}

  public static getInstance(): PurchasesService {
    if (!PurchasesService.instance) {
      PurchasesService.instance = new PurchasesService();
    }
    return PurchasesService.instance;
  }

  /**
   * Initialise RevenueCat avec les clés API.
   * Ne PAS appeler de hook React ici. Passer l'appUserID en paramètre si disponible.
   */
  async initialize(appUserID?: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const iosKey = process.env.NEXT_PUBLIC_REVENUECAT_API_KEY_IOS;
      const androidKey = process.env.NEXT_PUBLIC_REVENUECAT_API_KEY_ANDROID;

      let apiKey: string | undefined;

      // Choix de la clé par plateforme quand on est en natif
      if (this.isNativePlatform) {
        const cap =
          typeof window !== 'undefined'
            ? (window as any)?.Capacitor
            : undefined;
        let platform: string | undefined;
        try {
          if (cap) {
            if (typeof cap.getPlatform === 'function')
              platform = cap.getPlatform();
            else if (typeof cap.platform === 'string') platform = cap.platform;
          }
        } catch {
          // ignore
        }

        if (platform === 'ios') {
          apiKey = iosKey;
        } else if (platform === 'android') {
          apiKey = androidKey;
        }
      }

      if (!apiKey) {
        console.warn(
          'RevenueCat API key not found for current platform. Purchases will be disabled.'
        );
        return;
      }
      // Sur web (non natif), on n'initialise pas le plugin Capacitor
      if (this.isNativePlatform) {
        const { Purchases } = await import('@revenuecat/purchases-capacitor');
        await Purchases.configure({
          apiKey: apiKey,
          appUserID: appUserID, // RevenueCat générera automatiquement un ID utilisateur si non fourni
          useAmazon: false,
        });
      }

      this.isInitialized = true;
      this.currentAppUserID = appUserID;
      console.log('RevenueCat initialized successfully');
    } catch (error) {
      console.error('Failed to initialize RevenueCat:', error);
    }
  }

  /**
   * Met à jour l'utilisateur courant côté RevenueCat (logIn/logOut) de façon idempotente
   */
  async setAppUserId(appUserID?: string): Promise<void> {
    try {
      // Si pas de changement, ne rien faire
      if (this.currentAppUserID === (appUserID || undefined)) {
        return;
      }

      if (!appUserID) {
        await this.ensureInitialized();
        if (this.isNativePlatform) {
          const { Purchases } = await import('@revenuecat/purchases-capacitor');
          await Purchases.logOut();
        }
        this.currentAppUserID = undefined;
        // Réinitialiser le flag global
        if (typeof window !== 'undefined') {
          (window as any).__isPremiumUser = false;
        }
        return;
      }

      // Initialiser si pas encore fait
      if (!this.isInitialized) {
        await this.initialize(appUserID);
      }

      if (this.isNativePlatform) {
        const { Purchases } = await import('@revenuecat/purchases-capacitor');
        const { customerInfo } = await Purchases.logIn({ appUserID });
        void customerInfo;
      }

      this.currentAppUserID = appUserID;

      // Sur web, vérifier immédiatement le statut premium
      if (!this.isNativePlatform) {
        const status = await this.getSubscriptionStatus();
        if (typeof window !== 'undefined') {
          (window as any).__isPremiumUser = status.isPremium;
        }
      }
    } catch (error) {
      console.error('Failed to set app user id for RevenueCat:', error);
    }
  }

  /**
   * Récupère les offres disponibles
   */
  async getOfferings(): Promise<PurchasesOffering | null> {
    try {
      await this.ensureInitialized();
      if (!this.isNativePlatform) {
        // Pas d'offres côté web (pas d'achat in-app)
        return null;
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const offerings = await Purchases.getOfferings();
      return offerings.current;
    } catch (error) {
      console.error('Failed to get offerings:', error);
      return null;
    }
  }

  /**
   * Effectue un achat
   */
  async purchasePackage(
    packageToPurchase: PurchasesPackage
  ): Promise<CustomerInfo | null> {
    try {
      await this.ensureInitialized();
      if (!this.isNativePlatform) {
        console.warn('purchasePackage called on web: operation not supported');
        return null;
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const { customerInfo } = await Purchases.purchasePackage({
        aPackage: packageToPurchase,
      });
      return customerInfo;
    } catch (error) {
      console.error('Purchase failed:', error);
      return null;
    }
  }

  /**
   * Restaure les achats
   */
  async restorePurchases(): Promise<CustomerInfo | null> {
    try {
      await this.ensureInitialized();
      if (!this.isNativePlatform) {
        console.warn('restorePurchases called on web: operation not supported');
        return null;
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const { customerInfo } = await Purchases.restorePurchases();
      return customerInfo;
    } catch (error) {
      console.error('Restore purchases failed:', error);
      return null;
    }
  }

  /**
   * Vérifie le statut d'abonnement de l'utilisateur
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      await this.ensureInitialized();
      // Web: interroger notre API serveur qui appelle RevenueCat avec la clé secrète
      if (!this.isNativePlatform) {
        const appUserID = this.currentAppUserID;
        if (!appUserID) {
          return { isSubscribed: false, isPremium: false };
        }

        const resp = await fetch(
          `/api/revenuecat/subscriber?appUserID=${encodeURIComponent(appUserID)}`
        );
        if (!resp.ok) {
          throw new Error(`Failed to fetch subscriber: ${resp.status}`);
        }
        const data = await resp.json();

        const entitlements = data?.subscriber?.entitlements || {};
        const now = new Date();
        let isSubscribed = false;
        let currentPlan: string | undefined;
        let expirationDate: Date | undefined;

        for (const ent of Object.values<any>(entitlements)) {
          const expiresAt = ent?.expires_date
            ? new Date(ent.expires_date)
            : undefined;
          const unsubscribeAt = ent?.unsubscribe_detected_at
            ? new Date(ent.unsubscribe_detected_at)
            : undefined;
          const isActive =
            (!expiresAt || expiresAt > now) &&
            (!unsubscribeAt || unsubscribeAt > now);

          if (isActive) {
            isSubscribed = true;
            currentPlan = ent?.product_identifier || currentPlan;
            if (expiresAt) {
              expirationDate = expiresAt;
            }
            break;
          }
        }

        // Forcer la mise à jour du flag global côté web
        if (typeof window !== 'undefined') {
          (window as any).__isPremiumUser = isSubscribed;
        }

        return {
          isSubscribed,
          isPremium: isSubscribed,
          currentPlan,
          expirationDate,
        };
      }

      // Natif: utiliser le SDK Capacitor
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const { customerInfo } = await Purchases.getCustomerInfo();

      const activeEntitlements = Object.values(
        customerInfo.entitlements?.active || {}
      );
      const isSubscribed = activeEntitlements.length > 0;
      const isPremium = isSubscribed;

      let currentPlan: string | undefined;
      let expirationDate: Date | undefined;

      if (isSubscribed) {
        const entitlement = activeEntitlements[0] as any;
        currentPlan = entitlement.productIdentifier;
        if (entitlement.expirationDate) {
          expirationDate = new Date(entitlement.expirationDate);
        }
      }

      return {
        isSubscribed,
        isPremium,
        currentPlan,
        expirationDate,
      };
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        isSubscribed: false,
        isPremium: false,
      };
    }
  }

  /**
   * S'assure que RevenueCat est initialisé
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize(this.currentAppUserID);
    }
  }

  /**
   * Écoute les changements d'abonnement
   */
  async addCustomerInfoUpdateListener(
    callback: (customerInfo: CustomerInfo) => void
  ): Promise<void> {
    try {
      await this.ensureInitialized();
      if (!this.isNativePlatform) {
        // Côté web: pas d'événements push. On fera des refresh manuels.
        return;
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const listenerId =
        await Purchases.addCustomerInfoUpdateListener(callback);
      this.customerInfoListenerMap.set(callback, listenerId);
    } catch (error) {
      console.error('Failed to add customer info update listener:', error);
    }
  }

  /**
   * Supprime l'écouteur de changements d'abonnement
   */
  async removeCustomerInfoUpdateListener(
    callback: (customerInfo: CustomerInfo) => void
  ): Promise<void> {
    try {
      await this.ensureInitialized();
      if (!this.isNativePlatform) {
        return;
      }
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const listenerId = this.customerInfoListenerMap.get(callback);
      if (listenerId) {
        await Purchases.removeCustomerInfoUpdateListener({
          listenerToRemove: listenerId,
        });
        this.customerInfoListenerMap.delete(callback);
      }
    } catch (error) {
      console.error('Failed to remove customer info update listener:', error);
    }
  }
}

// Export d'une instance singleton
export const purchasesService = PurchasesService.getInstance();
